import { Controller } from "@hotwired/stimulus";

// Recenters the active step when the nav is horizontally overflowing; visual state is handled server-side
const NAV_ITEM_SELECTOR = "[data-hs-stepper-nav-item]";
const POINTER_MOVE_OPTIONS = { passive: false };
const MAX_RECENTER_ATTEMPTS = 10;
const RECENTER_RETRY_MS = 100;

export default class extends Controller {
  static targets = ["scroll", "item"];
  static values = { currentIndex: Number, clickable: Boolean };

  connect() {
    // For cleanup of listeners on disconnect.
    this._disposers = [];

    const initialCompleted = this.initialCompletedIndicesFromDataAttrs();
    const isResume = this.currentIndexValue > 1 || initialCompleted.length > 0;
    if (isResume) this.applyStepperState(this.currentIndexValue, initialCompleted, []);

    // SurveyJS bridge (top_stepper:change) will apply completed/missing once it has session state.
    this._setupRecenter();

    this.enableMouseDragScroll();

    this._setupBridge();
    this._setupClickableNavigation();
  }

  disconnect() {
    this._disposeAll();
    this.disableMouseDragScroll();
  }

  _setupRecenter() {
    this._onRecenter = this.centerActiveOnMobile.bind(this);
    this._listen(window, "load", this._onRecenter);
    this._listen(document, "turbo:load", this._onRecenter);
    this._listen(window, "resize", this._onRecenter);
    this.centerActiveOnMobile();
  }

  _setupBridge() {
    // Bridge: allow other code (e.g. SurveyJS navigation) to update this stepper.
    // Expected detail: {
    //   currentIndex: Number,
    //   completedIndices?: Number[],
    //   missingIndices?: Number[],
    //   visiblePageNames?: String[]
    // }
    // where currentIndex is 1-based.
    this._onTopStepperChange = (event) => {
      const nextIndex = Number(event?.detail?.currentIndex);
      if (!Number.isFinite(nextIndex) || nextIndex < 1) return;

      const completedIndices = Array.isArray(event?.detail?.completedIndices)
        ? event.detail.completedIndices
        : [];
      const missingIndices = Array.isArray(event?.detail?.missingIndices)
        ? event.detail.missingIndices
        : [];

      const visiblePageNames = Array.isArray(event?.detail?.visiblePageNames)
        ? event.detail.visiblePageNames
        : null;
      // Prefer titles for DOM reconciliation (handles unnamed pages via fallback title),
      // but keep backward-compatibility with `visiblePageNames`.
      const visibleStepTitles = Array.isArray(event?.detail?.visibleStepTitles)
        ? event.detail.visibleStepTitles
        : null;
      if (visibleStepTitles) this.applyVisibleStepNames(visibleStepTitles);
      else if (visiblePageNames) this.applyVisibleStepNames(visiblePageNames);

      this.setCurrentIndex(nextIndex, completedIndices, missingIndices);
    };
    this._listen(document, "top_stepper:change", this._onTopStepperChange);
  }

  _setupClickableNavigation() {
    // If the stepper is not clickable, don't bind any navigation behavior.
    if (!this.clickableValue) return;

    // Allow clicking a step to request navigation (SurveyJS lives elsewhere; we bridge via events).
    this._onClick = (e) => {
      const resolvedItem = this.resolveNavItemFromClickEvent(e);
      if (!resolvedItem) return;

      const index = Number(resolvedItem.dataset.index);
      if (!Number.isFinite(index) || index < 1) return;

      // If the click lands on a link, prevent navigation; SurveyJS will handle in-page routing.
      e.preventDefault?.();
      document.dispatchEvent(new CustomEvent("top_stepper:navigate", { detail: { index } }));
    };
    this._listen(this.element, "click", this._onClick);
  }

  // Center the active/current step when the nav is horizontally overflowing.
  // NOTE: legacy name (previously mobile-only).
  centerActiveOnMobile(attempt = 0, previousIndex = null) {
    if (!this.hasScrollTarget) return;
    if (!this.hasItemTarget) return;

    const container = this.scrollTarget;
    const active = this.itemTargets.find(
      (el) => parseInt(el.dataset.index, 10) === this.currentIndexValue
    );
    if (!active) return;

    // If no horizontal overflow yet, retry briefly (layout/Preline may still be settling)
    if (container.scrollWidth <= container.clientWidth) {
      if (attempt < MAX_RECENTER_ATTEMPTS) {
        setTimeout(
          () => this.centerActiveOnMobile(attempt + 1, previousIndex),
          RECENTER_RETRY_MS
        );
      }
      return;
    }

    const centerEl = active.querySelector?.("[data-top_stepper-wrapper]") || active;

    // Defer until layout is stable then compute delta using bounding rects
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const activeRect = centerEl.getBoundingClientRect();
      const containerCenterX = containerRect.left + containerRect.width / 2;
      const activeCenterX = activeRect.left + activeRect.width / 2;
      const delta = activeCenterX - containerCenterX;

      if (Math.abs(delta) < 1) return;

      // Recenter rule:
      // - moving forward: only scroll once the active step is past the midpoint (avoid noisy recentering)
      // - moving backward: recenter once the active step is before the midpoint (so "Previous" recenters)
      // - unknown direction (initial load / resize / reorder): recenter in either direction
      const prev = Number(previousIndex);
      const curr = Number(this.currentIndexValue);
      const movingForward = Number.isFinite(prev) && Number.isFinite(curr) && curr > prev;
      const movingBackward = Number.isFinite(prev) && Number.isFinite(curr) && curr < prev;

      if (movingForward && delta <= 1) return;
      if (movingBackward && delta >= -1) return;
      container.scrollTo({ left: container.scrollLeft + delta, behavior: "smooth" });
    });
  }

  // Center an arbitrary nav item (e.g. the clicked step) when overflowing.
  centerNavItem(item, attempt = 0) {
    if (!item) return;
    if (!this.hasScrollTarget) return;

    const container = this.scrollTarget;
    if (container.scrollWidth <= container.clientWidth) return;

    // If the nav item was clicked while layout is still settling, retry briefly.
    if (attempt < MAX_RECENTER_ATTEMPTS && container.scrollWidth <= container.clientWidth) {
      setTimeout(() => this.centerNavItem(item, attempt + 1), RECENTER_RETRY_MS);
      return;
    }

    const centerEl = item.querySelector?.("[data-top_stepper-wrapper]") || item;
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const itemRect = centerEl.getBoundingClientRect();
      const containerCenterX = containerRect.left + containerRect.width / 2;
      const itemCenterX = itemRect.left + itemRect.width / 2;
      const delta = itemCenterX - containerCenterX;
      if (Math.abs(delta) < 1) return;
      container.scrollTo({ left: container.scrollLeft + delta, behavior: "smooth" });
    });
  }

  setCurrentIndex(nextIndex, completedIndices = [], missingIndices = []) {
    const previousIndex = this.currentIndexValue;
    // Keep the stimulus value in sync so centering logic has the right current index.
    this.currentIndexValue = nextIndex;
    this.applyStepperState(nextIndex, completedIndices, missingIndices);
    this.centerActiveOnMobile(0, previousIndex);
  }

  applyStepperState(currentIndex, completedIndices = [], missingIndices = []) {
    const completedSet = new Set(completedIndices.map((n) => Number(n)).filter(Number.isFinite));
    const missingSet = new Set(missingIndices.map((n) => Number(n)).filter(Number.isFinite));

    // We must toggle `.active` / `.success` on the nav <li> items.
    // IMPORTANT: Never apply both active+success to the same element, otherwise both "You are here"
    // and "Completed" blocks can show at once.
    const navItems = this.element.querySelectorAll(NAV_ITEM_SELECTOR);
    navItems.forEach((el) => {
      const idx = parseInt(el.dataset.index, 10);

      const isActive = idx === currentIndex;
      const isMissing = missingSet.has(idx);
      const isSuccess = completedSet.has(idx) && !isMissing;

      // `completed` is a historical class name in some steppers; we clear it defensively.
      el.classList.remove(
        "active",
        "success",
        "completed",
        "missing",
        "hs-stepper-active",
        "hs-stepper-success",
        "hs-stepper-completed"
      );

      if (isActive) el.classList.add("active", "hs-stepper-active");
      else if (isSuccess) el.classList.add("success", "hs-stepper-success");

      // Missing can coexist with "active" (current page has errors) but should never be "success".
      if (isMissing) el.classList.add("missing");
    });
  }

  // Server renders steps from the full SurveyJS `pages` list; SurveyJS runtime uses `visiblePages`.
  // On initial load (before the first autosave), Rails can't know which pages are hidden by visibility rules,
  // so we prune/reorder/renumber the DOM to match the `visiblePages` list emitted by addNavigation.js.
  applyVisibleStepNames(visiblePageNames) {
    const names = visiblePageNames.filter((n) => typeof n === "string" && n.length > 0);
    if (names.length === 0) return;

    const nameSet = new Set(names);
    // Title can (rarely) be duplicated (e.g. missing page.name fallback). Keep a queue per title
    // so we don't accidentally drop earlier items.
    const byTitle = new Map();
    this.itemTargets.forEach((item) => {
      const title = item.dataset.stepTitle;
      if (!title) return;
      const existing = byTitle.get(title);
      if (existing) existing.push(item);
      else byTitle.set(title, [item]);
    });

    // Hide any server-rendered steps that aren't visible, and re-order to match SurveyJS.
    this.itemTargets.forEach((item) => {
      const title = item.dataset.stepTitle;
      item.classList.toggle("hidden", !title || !nameSet.has(title));
    });

    const ul = this.element.querySelector("ul");
    if (!ul) return;

    const ordered = names
      .map((n) => byTitle.get(n)?.shift?.())
      .filter(Boolean);
    ordered.forEach((item) => ul.appendChild(item));

    // Renumber visible items to 1..N so the stepper indices match SurveyJS visiblePages order.
    ordered.forEach((item, i) => {
      const index = i + 1;
      item.dataset.index = String(index);

      const num = item.querySelector("[data-top_stepper-number]");
      if (num && !item.classList.contains("active")) {
        num.textContent = String(index);
      }

      // Hide the right connector on the last *visible* step (server may have rendered more steps after it).
      const right = item.querySelector("[data-top_stepper-wrapper]")?.querySelectorAll(".self-center.w-full.min-w-\\[12px\\].h-px.flex-1.bg-slate-400")?.[1];
      if (right) {
        right.classList.toggle("invisible", index === ordered.length);
      }
    });

    // Filtering/reordering can change overflow; recenter if needed.
    this.centerActiveOnMobile();
  }

  initialCompletedIndicesFromDataAttrs() {
    const indices = [];
    const navItems = this.element.querySelectorAll(NAV_ITEM_SELECTOR);
    navItems.forEach((el) => {
      try {
        const raw = el.getAttribute("data-hs-stepper-nav-item");
        if (!raw) return;
        const data = JSON.parse(raw);
        if (data?.isCompleted) indices.push(Number(data.index));
      } catch (_) {
        // ignore invalid JSON; we can still update from events later
      }
    });
    return indices.filter(Number.isFinite);
  }

  resolveNavItemFromClickEvent(e) {
    // Prefer our own stable markup over Preline attributes, since clicks can land on nested nodes.
    const navItem =
      e.target?.closest?.('[data-top_stepper-target="item"]') ||
      e.target?.closest?.(NAV_ITEM_SELECTOR) ||
      e.target?.closest?.("[data-index]");

    if (navItem && this.element.contains(navItem)) return navItem;

    // Fallback: sometimes clicks land on the scrolling <ul> gap/connector but are still visually
    // "on" a card. Hit-test against known items using the click coordinates.
    if (!this.hasItemTarget) return null;
    const x = e.clientX;
    const y = e.clientY;
    return (
      this.itemTargets.find((el) => {
        const r = el.getBoundingClientRect();
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      }) || null
    );
  }

  enableMouseDragScroll() {
    if (!this.hasScrollTarget) return;

    const container = this.scrollTarget;
    this._drag = {
      container,
      isDown: false,
      didMove: false,
      startX: 0,
      startScrollLeft: 0,
      prevUserSelect: null
    };

    // Cursor / selection affordances.
    container.classList.add("cursor-grab", "select-none");

    this._onPointerDown = (e) => {
      // Mouse only: touch should keep native inertial scrolling.
      if (e.pointerType !== "mouse") return;
      // Primary button only.
      if (typeof e.button === "number" && e.button !== 0) return;

      this._drag.isDown = true;
      this._drag.didMove = false;
      this._drag.startX = e.clientX;
      this._drag.startScrollLeft = container.scrollLeft;

      // Capture so we keep getting events even if pointer leaves the element.
      container.setPointerCapture?.(e.pointerId);
      container.classList.add("cursor-grabbing");
      container.classList.remove("cursor-grab");
    };

    this._onPointerMove = (e) => {
      if (!this._drag?.isDown) return;
      if (e.pointerType !== "mouse") return;

      const dx = e.clientX - this._drag.startX;

      // Only treat as a drag once we’ve moved meaningfully; this preserves click behavior.
      if (!this._drag.didMove && Math.abs(dx) > 6) {
        this._drag.didMove = true;
        this._drag.prevUserSelect = document.body.style.userSelect;
        document.body.style.userSelect = "none";
      }

      if (!this._drag.didMove) return;

      // Prevent text selection / click-through while dragging.
      e.preventDefault();
      container.scrollLeft = this._drag.startScrollLeft - dx;
    };

    this._onPointerUp = (e) => {
      if (e.pointerType !== "mouse") return;

      if (this._drag) this._drag.isDown = false;
      container.releasePointerCapture?.(e.pointerId);
      container.classList.add("cursor-grab");
      container.classList.remove("cursor-grabbing");

      if (this._drag?.didMove) {
        // Restore selection immediately, and cancel the next click (it would be accidental).
        document.body.style.userSelect = this._drag.prevUserSelect ?? "";
        this._drag.prevUserSelect = null;
        const cancelClickOnce = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        // `once` means we don't need explicit cleanup (and it only affects the single accidental click).
        container.addEventListener("click", cancelClickOnce, { capture: true, once: true });
      }
    };

    this._listen(container, "pointerdown", this._onPointerDown);
    // NOTE: non-passive so we can call preventDefault while dragging.
    this._listen(container, "pointermove", this._onPointerMove, POINTER_MOVE_OPTIONS);
    this._listen(container, "pointerup", this._onPointerUp);
    this._listen(container, "pointercancel", this._onPointerUp);
    this._listen(container, "lostpointercapture", this._onPointerUp);
  }

  disableMouseDragScroll() {
    const container = this._drag?.container;
    if (!container) return;

    // If we navigated away mid-drag, ensure we restore global selection.
    if (this._drag.prevUserSelect !== null) {
      document.body.style.userSelect = this._drag.prevUserSelect ?? "";
    }

    container.classList.remove("cursor-grabbing", "cursor-grab", "select-none");

    this._drag = null;
    this._onPointerDown = null;
    this._onPointerMove = null;
    this._onPointerUp = null;
  }

  _listen(target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    this._disposers.push(() => target.removeEventListener(eventName, handler, options));
  }

  _disposeAll() {
    const disposers = this._disposers || [];
    this._disposers = null;
    disposers.forEach((dispose) => dispose());

    this._onRecenter = null;
    this._onTopStepperChange = null;
    this._onClick = null;
  }
}
